/* eslint-disable max-len */
import cn from 'classnames';
import React, { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Chat from 'src/components/base-components/Chat/Chat';
import Iframe from 'src/components/base-components/Iframe';
import MobileFixedMenu from 'src/components/base-components/OnlyMobileComponents/MobileFixedMenu';
import MobileMenu from 'src/components/base-components/OnlyMobileComponents/MobileMenu';
import MobileProviders from 'src/components/base-components/OnlyMobileComponents/MobileProviders';
import MobileSearch from 'src/components/base-components/OnlyMobileComponents/MobileSearch';
import Scrollbar from 'src/components/common/Scrollbar/ScrollBar';
import { FooterConstructor } from 'src/constructors/Footer/FooterConstructor';
import { HeaderConstructor } from 'src/constructors/Header/HeaderConstructor';
import DesktopSideBarConstructor from 'src/constructors/SideBarNavigation/DesktopSideBarConstructor';
import { getModuleFrames } from 'src/helpers/getModuleFrames';
import { detectDevice, envBoolean, getFromPath, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { usePostMesssage } from 'src/hooks/use-postMessage';
import SectionLoaderPage from 'src/pages/SectionLoaderPage';
import { getMenuWidget } from 'src/services/cmsApi';
import { setSelectedCategory } from 'src/store/app/actions';
import { setFreeSpinGames, setGameBoxMissions } from 'src/store/integrations/actions';
import { CmsMenuWidgetItem } from 'src/types/cms/cmsWidgetTypes';
import { RootState } from 'src/types/store-types';
import { IFRAMES } from 'src/utils/constants';
import { lazyWithCustomError } from 'src/utils/lazyWithCustomError';
import './style.scss';

const SkinBuilder = lazyWithCustomError(() => import('src/components/base-components/SkinBuilder'));

const MainLayout: FC<any> = (props) => {
  const device = detectDevice();
  const { pathname } = useLocation();
  const { locale } = useI18n();

  const _freeSpinGamesReq = useConnectedAction(setFreeSpinGames.req);
  const _setGameBoxMission = useConnectedAction(setGameBoxMissions.req);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);

  const { showMenu, showSearch, showProviders } = useSelector((state: RootState) => state.mobile);
  const { user } = useSelector((state: RootState) => state.user);
  const { selectedCategory, sectionLoading, sideBarMenuIsOpen } = useSelector((state: RootState) => state.app);
  const { generalConfigs, navigation, headerTemplate } = useSelector((state: RootState) => state.configs);
  const [selectedDevice, setSelectedDevice] = useState<'desktop' | 'tablet' | 'mobile'>(device);
  const [mobileMenuWidgetItems, setMobileMenuWidgetItems] = useState<CmsMenuWidgetItem[]>([]);
  const location = useLocation();
  const currentUrl = `${window.location.origin}${location.pathname}`;

  const renderHelmet = (category: string): any => {
    switch (category) {
      case 'betting':
        return (
          <HelmetProvider>
            <Helmet>
              <title>Apostas esportivas</title>
              <meta
                name="description"
                content="Aposte nos melhores esportes online com odds ao vivo em futebol, basquete, UFC e mais. Tenha acesso às últimas odds de apostas e experimente a emoção das apostas em tempo real!"
              />
              <meta
                name="keywords"
                content="Aposte nos melhores esportes online com odds ao vivo em futebol, basquete, UFC e mais. Tenha acesso às últimas odds de apostas e experimente a emoção das apostas em tempo real!"
              />
              <link rel="canonical" href={currentUrl} />
            </Helmet>
          </HelmetProvider>
        );
      case 'live-sport':
        return (
          <HelmetProvider>
            <Helmet>
              <title>Apostas esportivas ao vivo – Futebol, basquete, hóquei, tênis, e-sports e muito mais!</title>
              <meta
                name="description"
                content="Aposte ao vivo em ligas de futebol, basquete, hóquei, tênis, sinuca e e-sports na Papi Games. Experimente probabilidades em tempo real e ação emocionante em todos os seus esportes favoritos. Inscreva-se agora para as melhores oportunidades de apostas ao vivo!"
              />
              <meta
                name="keywords"
                content="Aposte ao vivo em ligas de futebol, basquete, hóquei, tênis, sinuca e e-sports na Papi Games. Experimente probabilidades em tempo real e ação emocionante em todos os seus esportes favoritos. Inscreva-se agora para as melhores oportunidades de apostas ao vivo!"
              />
              <link rel="canonical" href={currentUrl} />
            </Helmet>
          </HelmetProvider>
        );
      case 'casino-tournament':
        return (
          <HelmetProvider>
            <Helmet>
              <title>
                Participe do PAPI SHOW & Game of Slots – Ganhe um prêmio total de $ 222.000 ou $ 22.000 diariamente!
              </title>
              <meta
                name="description"
                content="Participe do torneio diário PAPI SHOW para ter a chance de ganhar um prêmio de $222.000. Jogue o Game of Slots e ganhe até $22.000 diariamente. Entre agora para grandes vitórias!"
              />
              <meta
                name="keywords"
                content="Participe do torneio diário PAPI SHOW para ter a chance de ganhar um prêmio de $222.000. Jogue o Game of Slots e ganhe até $22.000 diariamente. Entre agora para grandes vitórias!"
              />
              <link rel="canonical" href={currentUrl} />
            </Helmet>
          </HelmetProvider>
        );
      default:
        return (
          <>
            <HelmetProvider>
              <Helmet>
                <link rel="canonical" href={currentUrl} />
              </Helmet>
            </HelmetProvider>
          </>
        );
    }
  };

  const isIframe = [...IFRAMES, ...getModuleFrames()].includes(selectedCategory);
  const isWagerFrame = ['betting', 'sport', 'live-sport', 'liveSport'].includes(selectedCategory);
  const hasSkinBuilder =
    envBoolean(process.env.REACT_APP_SKIN_BUILDER_MODE as string) && !localStorage.getItem('dontShowSkinBuilderTool');

  useEffect(() => {
    window.onpopstate = function () {
      const newPathname = window.location.pathname;
      const newSelectedTab = getFromPath(newPathname, '/', 2);
      if (newSelectedTab) {
        _setSelectedCategory(newSelectedTab);
      } else {
        _setSelectedCategory('home');
      }
    };
  }, []);

  useEffect(() => {
    setSelectedDevice(device);
    isMobileOnly && setBodyOverflow([...IFRAMES, ...getModuleFrames()].includes(selectedCategory) ? 'unset' : 'set');
  }, [device, selectedCategory]);

  useEffect(() => {
    if (user) {
      _freeSpinGamesReq();
      _setGameBoxMission();
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    if (selectedDevice !== 'mobile') return () => undefined;

    getMenuWidget(locale).then((res) => {
      if (!isMounted || !res.success) return;

      const menuItems = res.result?.[0]?.content?.menuItems ?? [];
      setMobileMenuWidgetItems(menuItems);
    });

    return () => {
      isMounted = false;
    };
  }, [locale, selectedDevice]);

  usePostMesssage((v: any) => setSelectedDevice(v.device), 'deviceType');

  // eslint-disable-next-line sonarjs/cognitive-complexity
  return useMemo(() => {
    switch (selectedDevice) {
      case 'mobile':
        return (
          <div
            id={isIframe ? selectedCategory + 'Id' : ''}
            className={cn(selectedCategory, headerTemplate, 'mobile', {
              ['wager_wrapper']: isWagerFrame && envBoolean(process.env.REACT_APP_CMS_WAGER),
            })}
          >
            {hasSkinBuilder && (
              <Suspense fallback={null}>
                <SkinBuilder />
              </Suspense>
            )}
            <HeaderConstructor type="mobile" />
            {renderHelmet(selectedCategory)}
            {showMenu && <MobileMenu menuWidgetItems={mobileMenuWidgetItems} />}
            {showSearch && <MobileSearch />}
            {showProviders && <MobileProviders />}
            {envBoolean(process.env.REACT_APP_HAS_SECTION_LOADER as string) && !sectionLoading.includes(false) && (
              <SectionLoaderPage />
            )}
            {props.children}
            {navigation?.map(
              (category) => category?.preRenderIframe && <Iframe key={category.name} type={category.name} />
            )}
            <MobileFixedMenu />
            {!isIframe && <FooterConstructor type="mobile" />}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={cn(headerTemplate, 'layout_container', 'tablet', {
              ['layout_container__iframe']: isIframe,
              ['iframe_wrapper']: isIframe,
              ['wager_wrapper']: isWagerFrame && envBoolean(process.env.REACT_APP_CMS_WAGER),
            })}
          >
            <Scrollbar>
              <div className={cn(selectedCategory, headerTemplate, 'tablet')}>
                <HeaderConstructor type="tablet" />
                {renderHelmet(selectedCategory)}
                {envBoolean(process.env.REACT_APP_HAS_SECTION_LOADER as string) && !sectionLoading.includes(false) && (
                  <SectionLoaderPage />
                )}
                {props.children}
                {navigation?.map(
                  (category) => category?.preRenderIframe && <Iframe key={category.name} type={category.name} />
                )}
                {!!generalConfigs?.chat && <Chat configs={generalConfigs?.chat} />}
                <FooterConstructor type="tablet" />
              </div>
            </Scrollbar>
          </div>
        );
      default:
        if (envBoolean(process.env.REACT_APP_HAS_LOGIN_PAGE as string) && !user) {
          return null;
        } else {
          return (
            <div
              className={cn(headerTemplate, 'layout_container', 'desktop', {
                ['layout_container__iframe']: isIframe,
                ['sidebar_open']: sideBarMenuIsOpen,
                ['iframe_wrapper']: isIframe,
                ['wager_wrapper']: isWagerFrame && envBoolean(process.env.REACT_APP_CMS_WAGER),
              })}
            >
              {hasSkinBuilder && (
                <Suspense fallback={''}>
                  <SkinBuilder />
                </Suspense>
              )}
              <Scrollbar>
                <HeaderConstructor type="desktop" />
                {generalConfigs?.hasSideBar && <DesktopSideBarConstructor />}
                <div
                  className={cn(selectedCategory, 'desktop', 'page_content', {
                    ['sidebar_open']: sideBarMenuIsOpen,
                    ['iframe_wrapper']: isIframe,
                    ['wager_wrapper']: isWagerFrame && envBoolean(process.env.REACT_APP_CMS_WAGER),
                  })}
                  id={isIframe ? selectedCategory + 'Id' : ''}
                >
                  {renderHelmet(selectedCategory)}
                  {envBoolean(process.env.REACT_APP_HAS_SECTION_LOADER as string) &&
                    !sectionLoading.includes(false) && <SectionLoaderPage />}
                  {props.children}
                  {navigation?.map(
                    (category) => category?.preRenderIframe && <Iframe key={category.name} type={category.name} />
                  )}
                  {!!generalConfigs?.chat && <Chat configs={generalConfigs.chat} />}
                </div>
                <FooterConstructor type="desktop" />
              </Scrollbar>
            </div>
          );
        }
    }
  }, [
    selectedDevice,
    showMenu,
    showSearch,
    showProviders,
    selectedCategory,
    pathname,
    sectionLoading,
    props.children,
    sideBarMenuIsOpen,
    generalConfigs,
    sideBarMenuIsOpen,
    isIframe,
    headerTemplate,
    user,
    mobileMenuWidgetItems,
  ]);
};
export default MainLayout;
